import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { estagioService, termoRescisaoPdfService } from '@/services/api';
import { Estagio, EstagioFilters, PaginationResponse } from '@/types';
import { DocumentMagnifyingGlassIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';

// Função utilitária para formatar data sem problema de timezone
const formatarDataUTC = (dataString: string): string => {
  const data = new Date(dataString);
  const dia = String(data.getUTCDate()).padStart(2, '0');
  const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
  const ano = data.getUTCFullYear();
  return `${dia}/${mes}/${ano}`;
};

export const TermoRescisaoPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<EstagioFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { handleError } = useErrorHandler();
  const [loadError, setLoadError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEstagio, setSelectedEstagio] = useState<Estagio | null>(null);
  const [dataRescisao, setDataRescisao] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [motivoEncerramento, setMotivoEncerramento] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelEstagio, setCancelEstagio] = useState<Estagio | null>(null);
  const [dataCancelamento, setDataCancelamento] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const [reativandoId, setReativandoId] = useState<number | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const { data: estagiosData, isLoading, error } = useQuery<PaginationResponse<Estagio>>({
    queryKey: ['estagios-rescisao-pdf', filters],
    queryFn: () => termoRescisaoPdfService.listEstagios(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as Error, 'Erro ao carregar estágios');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (estagiosData) {
      setLoadError(null);
    }
  }, [estagiosData]);

  const clearFilters = () => {
    setFilters({ page: 1, limit: 10 });
    setSearchTerm('');
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const openModal = (estagio: Estagio) => {
    setSelectedEstagio(estagio);
    setDataRescisao(new Date().toISOString().split('T')[0]);
    setMotivoEncerramento('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedEstagio(null);
    setMotivoEncerramento('');
  };

  const openCancelModal = (estagio: Estagio) => {
    setCancelEstagio(estagio);
    setDataCancelamento(new Date().toISOString().split('T')[0]);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setCancelEstagio(null);
  };

  const handleGerarRescisaoPdf = async () => {
    if (!selectedEstagio) return;

    const estagioJaCancelado = selectedEstagio.status === 'Cancelado';
    const mensagemCarregando = estagioJaCancelado 
      ? 'Gerando termo de rescisão em PDF (estágio já cancelado)...'
      : 'Gerando termo de rescisão em PDF...';

    try {
      toast.loading(mensagemCarregando, { id: 'gerar-rescisao-pdf' });

      const response = await termoRescisaoPdfService.gerarRescisaoPdf(
        selectedEstagio.id,
        dataRescisao,
        estagioJaCancelado ? undefined : motivoEncerramento.trim() || undefined
      );

      // Verificar se a resposta é válida
      if (!response || response.size === 0) {
        throw new Error('PDF gerado está vazio');
      }

      // Criar URL do blob e abrir em nova aba
      const url = window.URL.createObjectURL(response);
      const novaAba = window.open(url, '_blank');

      if (!novaAba) {
        // Se o popup foi bloqueado, tentar download forçado
        const link = document.createElement('a');
        link.href = url;
        link.download = `termo_rescisao_${selectedEstagio.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('PDF baixado com sucesso (popup bloqueado)', { id: 'gerar-rescisao-pdf' });
      } else {
        const mensagemSucesso = estagioJaCancelado
          ? 'Termo de rescisão PDF gerado com sucesso! (usando data de cancelamento já salva)'
          : 'Termo de rescisão PDF gerado com sucesso! O estágio foi cancelado automaticamente.';
        
        toast.success(mensagemSucesso, { 
          id: 'gerar-rescisao-pdf',
          duration: 5000 
        });
      }

      // Limpar URL após um delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 2000);

      // Invalida o cache para atualizar a lista de estágios
      queryClient.invalidateQueries({ queryKey: ['estagios-rescisao-pdf'] });

      closeModal();
    } catch (error: unknown) {
      console.error('Erro ao gerar rescisão PDF:', error);
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError?.response?.data?.error || 'Erro ao gerar termo de rescisão PDF';
      toast.error(errorMessage, { id: 'gerar-rescisao-pdf' });
    }
  };

  const handleCancelarEstagio = async () => {
    if (!cancelEstagio) return;

    try {
      toast.loading('Cancelando estágio...', { id: 'cancelar-estagio' });

      await estagioService.cancelar(cancelEstagio.id, { dataCancelamento });

      toast.success('Estágio cancelado com sucesso!', { id: 'cancelar-estagio' });
      queryClient.invalidateQueries({ queryKey: ['estagios-rescisao-pdf'] });
      queryClient.invalidateQueries({ queryKey: ['estagios'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      closeCancelModal();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError?.response?.data?.error || 'Erro ao cancelar estágio';
      toast.error(errorMessage, { id: 'cancelar-estagio' });
    }
  };

  const handleReativarEstagio = (estagio: Estagio) => {
    showConfirm(
      'Reativar estágio?',
      `Desfazer o cancelamento de ${estagio.aluno?.nome || 'este estágio'} e voltar o status para Ativo? Um e-mail será enviado. Documentos de rescisão gerados por engano devem ser desconsiderados.`,
      async () => {
        try {
          setReativandoId(estagio.id);
          toast.loading('Reativando estágio...', { id: 'reativar-estagio' });
          await estagioService.reativar(estagio.id);
          toast.success('Estágio reativado com sucesso!', { id: 'reativar-estagio' });
          queryClient.invalidateQueries({ queryKey: ['estagios-rescisao-pdf'] });
          queryClient.invalidateQueries({ queryKey: ['estagios'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        } catch (error: unknown) {
          const axiosError = error as { response?: { data?: { error?: string } } };
          toast.error(axiosError?.response?.data?.error || 'Erro ao reativar estágio', {
            id: 'reativar-estagio',
          });
        } finally {
          setReativandoId(null);
        }
      },
      'warning'
    );
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Termo de Rescisão</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gere termos de rescisão em formato PDF (abre direto no navegador)
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span>Filtros</span>
            <span className="text-sm font-normal text-gray-500">
              ({estagiosData?.pagination.total || 0} estágios encontrados)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Aluno, empresa, instituição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full sm:w-auto sm:px-6"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estágios List */}
      <Card>
        <CardHeader>
          <CardTitle>Estágios Disponíveis</CardTitle>
          <CardDescription>
            Selecione um estágio para gerar o termo de rescisão em PDF (abre no navegador)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Carregando estágios...</p>
            </div>
          ) : estagiosData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum estágio encontrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {estagiosData?.data.map((estagio: Estagio) => {
                const possuiAgenteIntegrador = Boolean(estagio.agenteIntegradorId);

                return (
                  <div
                    key={estagio.id}
                    className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col gap-4">
                      {/* Informações do Estágio */}
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">
                          {estagio.aluno?.nome || 'Aluno não encontrado'}
                        </h3>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                          <p className="text-sm text-gray-600 truncate">
                            <span className="font-medium">Empresa:</span> {estagio.empresa?.nome || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            <span className="font-medium">Instituição:</span> {estagio.instituicao?.nome || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Tipo:</span> {estagio.tipo}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Início:</span> {formatarDataUTC(estagio.dataInicio)}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Término:</span> {formatarDataUTC(estagio.dataTermino)}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Status:</span>{' '}
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${estagio.status === 'Ativo'
                              ? 'bg-green-100 text-green-800'
                              : estagio.status === 'Cancelado'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                              }`}>
                              {estagio.status}
                            </span>
                          </p>
                          {possuiAgenteIntegrador && (
                            <p className="text-sm text-amber-700 sm:col-span-2 lg:col-span-3">
                              <span className="font-medium">Vinculado ao agente:</span> {estagio.agenteIntegrador?.nome || 'Agente integrador'}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Botão de Ação */}
                      <div className="flex flex-col sm:flex-row gap-2 justify-end sm:justify-start">
                        {estagio.status === 'Cancelado' ? (
                          <>
                            {!possuiAgenteIntegrador && (
                              <Button
                                variant="outline"
                                onClick={() => openModal(estagio)}
                                className="w-full sm:w-auto"
                              >
                                <DocumentMagnifyingGlassIcon className="h-4 w-4 sm:mr-2" />
                                <span className="ml-2 sm:ml-0">Ver PDF</span>
                              </Button>
                            )}
                            <Button
                              onClick={() => handleReativarEstagio(estagio)}
                              disabled={reativandoId === estagio.id}
                              className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                            >
                              <ArrowPathIcon className="h-4 w-4 sm:mr-2" />
                              <span className="ml-2 sm:ml-0">Reativar</span>
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={() => possuiAgenteIntegrador ? openCancelModal(estagio) : openModal(estagio)}
                            className={`w-full sm:w-auto ${possuiAgenteIntegrador ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
                          >
                            {possuiAgenteIntegrador ? (
                              <XCircleIcon className="h-4 w-4 sm:mr-2" />
                            ) : (
                              <DocumentMagnifyingGlassIcon className="h-4 w-4 sm:mr-2" />
                            )}
                            <span className="ml-2 sm:ml-0">{possuiAgenteIntegrador ? 'Cancelar' : 'Gerar Rescisão'}</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {estagiosData && estagiosData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!estagiosData.pagination.hasPrev}
                  className="px-3"
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Página {estagiosData.pagination.page} de {estagiosData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!estagiosData.pagination.hasNext}
                  className="px-3"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Rescisão */}
      {modalOpen && selectedEstagio && (() => {
        const estagioJaCancelado = selectedEstagio.status === 'Cancelado';
        
        // Função para formatar data sem problema de timezone
        const formatarDataParaInput = (dataString: string | undefined): string => {
          if (!dataString) return dataRescisao;
          
          // Criar data em UTC para evitar problema de timezone
          const data = new Date(dataString);
          const ano = data.getUTCFullYear();
          const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
          const dia = String(data.getUTCDate()).padStart(2, '0');
          
          return `${ano}-${mes}-${dia}`;
        };
        
        const dataCancelamentoFormatada = formatarDataParaInput(selectedEstagio.dataCancelamento);

        return (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={closeModal}
              />

              {/* Modal */}
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {estagioJaCancelado ? 'Visualizar Termo de Rescisão (PDF)' : 'Gerar Termo de Rescisão (PDF)'}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Aluno:</span> {selectedEstagio.aluno?.nome}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Empresa:</span> {selectedEstagio.empresa?.nome}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Status:</span>{' '}
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        estagioJaCancelado ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {selectedEstagio.status}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data da Rescisão {estagioJaCancelado ? '(já salva)' : '*'}
                    </label>
                    <input
                      type="date"
                      value={estagioJaCancelado ? dataCancelamentoFormatada : dataRescisao}
                      onChange={(e) => !estagioJaCancelado && setDataRescisao(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md ${
                        estagioJaCancelado 
                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                          : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                      }`}
                      disabled={estagioJaCancelado}
                      readOnly={estagioJaCancelado}
                      required
                    />
                    {estagioJaCancelado && (
                      <p className="text-xs text-gray-500 mt-1">
                        Esta é a data em que o estágio foi cancelado
                      </p>
                    )}
                  </div>

                  {estagioJaCancelado ? (
                    selectedEstagio.motivoEncerramento?.motivoPrincipal ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Motivo do encerramento
                        </label>
                        <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-sm text-gray-900 whitespace-pre-wrap">
                          {selectedEstagio.motivoEncerramento.motivoPrincipal}
                        </p>
                      </div>
                    ) : null
                  ) : (
                    <div>
                      <label htmlFor="motivo-encerramento" className="block text-sm font-medium text-gray-700 mb-2">
                        Motivo do encerramento (opcional)
                      </label>
                      <textarea
                        id="motivo-encerramento"
                        value={motivoEncerramento}
                        onChange={(e) => setMotivoEncerramento(e.target.value.slice(0, 255))}
                        maxLength={255}
                        rows={3}
                        placeholder="Ex.: aluno desistiu, término antecipado de comum acordo..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {motivoEncerramento.length}/255
                      </p>
                    </div>
                  )}

                  {estagioJaCancelado ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">ℹ️ Informação:</span> Este estágio já foi cancelado em {formatarDataUTC(selectedEstagio.dataCancelamento || '')}. O PDF será gerado com essa data.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-sm text-yellow-800">
                        <span className="font-medium">⚠️ Atenção:</span> O termo de rescisão será gerado em PDF e abrirá direto no navegador. <strong>O estágio será automaticamente cancelado</strong> com a data informada. Certifique-se de que a data está correta antes de prosseguir.
                      </p>
                    </div>
                  )}

                  <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                    <p className="text-sm text-purple-800">
                      <span className="font-medium">💡 Dica:</span> O PDF será gerado com cabeçalho e rodapé customizados (configurados em "Configuração de Termo").
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={closeModal}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleGerarRescisaoPdf}
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={!dataRescisao && !estagioJaCancelado}
                  >
                    <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
                    {estagioJaCancelado ? 'Visualizar PDF' : 'Gerar PDF'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Cancelamento */}
      {cancelModalOpen && cancelEstagio && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeCancelModal}
            />

            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cancelar Estágio
                </h3>
                <button
                  onClick={closeCancelModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Aluno:</span> {cancelEstagio.aluno?.nome}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Empresa:</span> {cancelEstagio.empresa?.nome}
                  </p>
                  <p className="text-sm text-amber-700">
                    <span className="font-medium">Agente:</span> {cancelEstagio.agenteIntegrador?.nome || 'Agente integrador'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data do Cancelamento *
                  </label>
                  <input
                    type="date"
                    value={dataCancelamento}
                    onChange={(e) => setDataCancelamento(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm text-amber-800">
                    Este estágio será marcado como Cancelado sem gerar termo de rescisão, sem consumir limite e sem enviar e-mail de rescisão.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={closeCancelModal}
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleCancelarEstagio}
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={!dataCancelamento}
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  Confirmar Cancelamento
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Reativar"
        cancelText="Voltar"
        isLoading={reativandoId !== null}
      />
    </div>
  );
};
